import { connect } from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import _findIndex from 'lodash/findIndex';
import _trim from 'lodash/trim';
import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';
import _differenceWith from 'lodash/differenceWith';
import _isEqual from 'lodash/isEqual';
import _slice from 'lodash/slice';
import {
    array,
    bool,
    element,
    elementType,
    func,
    number,
    object,
    oneOfType,
    string,
} from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { warning } from 'ravenjs/utils/warning';
import Pagination from 'ravenjs/lib/Pagination';
import Responsive from 'ravenjs/lib/Responsive';
import Col from 'ravenjs/lib/Col';
import { addSchemaValue } from 'ravenjs/utils/schema';
import Button from 'ravenjs/lib/Button';
import { get, isEmpty } from 'ravenjs/utils/lodash';
import { renderCheck } from 'ravenjs/utils/viewport';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';
import Checkbox from 'ravenjs/lib/CheckboxNew';

import { actions as uiActions } from 'modules/ui';
import { actions as adminActions } from 'modules/admin';
import { CheckboxEmptyIcon, CheckboxFilledIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { DATA_LIST_VIEW_SCHEMA } from 'constants/proptypes';
import MESSAGES from 'constants/messages';
import { COMPANY_LABEL, PARTNER_LABEL, USER_LOOKUP } from 'constants/tickets';
import { COMPANY_SEARCH, PARTNER_SEARCH, PARTNER_COMPANY_RESET } from 'constants/company';
import { RESET_ALL } from 'constants/common';
import { MODALS } from 'constants/modals';
import BackToTop from 'components/BackToTop';
import { Check } from 'containers/CaseCave/UserLookup/UserLookupCellTypes';
import ActionButtons from './ActionButtons';
import DataList, { DataListMemo } from './DataList';
import Filters from './Filters';
import SearchBox from './SearchBox';
import PartnerFilterSchema from '../../containers/Admin/Companies/List/FilterSchemas/PartnerFilterSchema.json';
import CompanyFilterSchema from '../../containers/Admin/Users/List/CompanyFilterSchema.json';

const SearchColStyled = styled(Col)`
    ${({ theme }) => theme.media.down('lg')`
        padding: 0;
    `};
    ${({ theme }) => theme.media.up('lg')`
        padding: none;
    `};
`;

function DataListView(props) {
    const {
        hideBackToTop,
        actions,
        data,
        customFirstRowData,
        dataFilters,
        defaultSelectedFilters,
        currentPage,
        schema,
        updateOnMount: updateOnMountProps,
        openModal,
        closeModal,
        fetchPartners,
        fetchClients,
        showPartnerSearch,
        resetAll,
        rightFilterColumnContent,
        useMemo,
        isStaticData,
        tableId,
        shouldClearSelectedRows,
    } = props;

    const onChangeHandler = getHandler(actions, get(schema, 'onChangeRef'));
    const actionButtons = get(schema, 'actionButtons', null);
    const selectedSchema = get(schema, 'selected', {});
    const sortingOptions = get(schema, 'sorting.options', {});
    const showScroll = get(schema, 'showScroll', false);
    const scrollStyles = get(schema, 'scrollStyles', false);
    const [selectedFilters, setSelectedFilters] = React.useState(dataFilters);
    const dateValidation = get(schema, 'dateValidation', null);
    const [order, setOrder] = React.useState(get(schema, 'sorting.default.order', ''));
    const [orderBy, setOrderBy] = React.useState(get(schema, 'sorting.default.orderBy', ''));
    const [page, setPage] = React.useState(get(schema, 'pagination.startPage', 1));
    const [rowsPerPage, setRowsPerPage] = React.useState(get(schema, 'pagination.rowsPerPage', 10));
    const [searchSpec, setSearchSpec] = React.useState(get(schema, 'search.value', ''));
    const [filterOptionSelected, setFilterOptionSelected] = React.useState(!isEmpty(dataFilters));
    const filterOptions = get(schema, 'filterOptions', {});
    const [phoneSearchSpec, setPhoneSearchSpec] = React.useState(
        get(schema, 'phoneSearch.value', '')
    );
    const [, setDefaultSortOption] = React.useState(get(schema, 'sorting.defaultOptions'));
    const defaultSelectedItems = get(schema, 'selected.items', []);
    const defaultSelectedItemsInfo = get(schema, 'selected.itemsInfo', []);
    const defaultSelectedAllItems = get(schema, 'selected.selectedAll', false);
    const shouldRenderSelectable = Boolean(get(schema, 'selected', null));
    const isMultiSelectEnabled = Boolean(get(schema, 'selected.isMulti', null));
    const isMultiSelectByPageEnabled = Boolean(get(schema, 'selected.isMultiSelectByPage', null));
    const selectableType = get(schema, 'selected.selectableType', null);
    if (shouldRenderSelectable) {
        addSchemaValue(schema, 'selected.renderSelectableRef', 'renderSelectable');
    }
    const [selectedAll, setSelectedAll] = React.useState(defaultSelectedAllItems);
    const [selected, setSelected] = React.useState(defaultSelectedItems);
    const [selectedInfo, setSelectedInfo] = React.useState(defaultSelectedItemsInfo);
    const [excluded, setExcluded] = React.useState([]);
    const [partnerId, setPartnerIds] = React.useState([]);
    const [companyId, setCompanyIds] = React.useState([]);
    const [searchInput, setSearchInput] = React.useState(get(schema, 'search.value', ''));
    const [phoneSearchInput, setPhoneSearchInput] = React.useState(
        get(schema, 'phoneSearch.value', '')
    );
    const [checkedItems, setCheckedItems] = React.useState({});

    const updateOnMount = React.useRef(updateOnMountProps);
    React.useEffect(() => {
        if (updateOnMount.current) {
            const currentState = {
                order,
                orderBy,
                page,
                rowsPerPage,
                searchSpec: _trim(searchSpec).length === 0 ? '' : _trim(searchSpec),
                phoneSearchSpec: _trim(phoneSearchSpec).length === 0 ? '' : _trim(phoneSearchSpec),
                totalRows: get(schema, 'pagination.totalRows', 0),
                partnerId,
                companyId,
            };
            callFunc(onChangeHandler, currentState);
        } else {
            updateOnMount.current = true;
        }
    }, [
        onChangeHandler,
        order,
        orderBy,
        page,
        rowsPerPage,
        schema,
        searchSpec,
        phoneSearchSpec,
        partnerId,
        companyId,
    ]);

    React.useEffect(() => {
        if (currentPage) {
            setPage(currentPage);
        }
    }, [currentPage, setPage]);

    React.useEffect(() => {
        const onUpdateFilters = get(actions, 'onUpdateFilters', () => {});
        callFunc(onUpdateFilters, selectedFilters);
    }, [actions, selectedFilters]);

    React.useEffect(() => {
        if (shouldClearSelectedRows) {
            setSelectedAll(false);
            setSelected([]);
            setSelectedInfo([]);
            setExcluded([]);
            setCheckedItems({});
        }
    }, [shouldClearSelectedRows]);

    if (isEmpty(schema)) {
        warning(!isEmpty(schema), [
            `A <DataListView /> requires a 'schema' prop to build the Data List Table.`,
        ]);
        return null;
    }

    const {
        filters = [],
        list = {},
        customFirstRow = {},
        pagination = {},
        refs = {},
        search = {},
        phoneSearch = {},
        sorting = {},
    } = schema;
    const { searchBoxStyles = {} } = search;
    const { position = null } = pagination;
    const paginationPositionBottom = position === 'bottom' || position === 'both';
    const paginationPositionTop = position === 'top' || position === 'both';

    function renderBottomPagination() {
        return paginationPositionBottom ? renderPagination() : null;
    }

    function renderTopPagination() {
        return paginationPositionTop ? renderPagination() : null;
    }

    function handleSearchOnChange(event) {
        setSearchInput(event.target.value);
    }

    function handlePhoneSearchOnChange(event) {
        setPhoneSearchInput(event.target.value);
    }

    function handleSearchOnKeyPress(e) {
        if (e.charCode === 13) {
            e.preventDefault();
            e.target.blur();
            handleSearch();
        }
    }

    function handlePhoneSearchOnKeyPress(e) {
        if (e.charCode === 13) {
            e.preventDefault();
            e.target.blur();
            handlePhoneSearch();
        }
    }

    function handleSearchReset() {
        if (actions.handleResetAll) {
            callFunc(actions.handleResetAll);
        } else {
            setSearchSpec('');
            setSearchInput('');
            setPage(1);
        }
    }

    function handlePhoneSearchReset() {
        if (actions.handleResetAll) {
            callFunc(actions.handleResetAll);
        } else {
            setPhoneSearchSpec('');
            setPhoneSearchInput('');
            setPage(1);
        }
    }

    const searchInputProps = {
        onKeyPress: handleSearchOnKeyPress,
        onChange: handleSearchOnChange,
        value: searchInput || '',
        margin: '0',
        inputContainerMarginRight: search.inputContainerMarginRight,
    };

    const phoneSearchInputProps = {
        onKeyPress: handlePhoneSearchOnKeyPress,
        onChange: handlePhoneSearchOnChange,
        value: phoneSearchInput || '',
    };

    function handleSortChange(currentOrder, newOrderBy) {
        const newOrder = currentOrder && currentOrder === 'asc' ? 'desc' : 'asc';
        const defaultClickOrder = get(schema, 'sorting.default.defaultClickOrder', 'asc');

        if (newOrder && orderBy) {
            setOrder(orderBy === newOrderBy ? newOrder : defaultClickOrder);
            setOrderBy(newOrderBy);
            const index = _findIndex(sortingOptions, { orderBy: newOrderBy });
            setDefaultSortOption(sortingOptions[index]);
            setPage(1);
        }
    }

    function renderSelectable(row) {
        if (!isMultiSelectEnabled && row === 'head') {
            return null;
        }
        if (row === 'head') {
            return (
                <Checkbox
                    checked={selectedAll}
                    type={selectableType || 'checkbox'}
                    icon={CheckboxEmptyIcon}
                    checkedIcon={CheckboxFilledIcon}
                    onChange={handleOnSelectAll}
                />
            );
        }
        return (
            <Checkbox
                checked={handleRowSelected(row)}
                type={selectableType || 'checkbox'}
                icon={CheckboxEmptyIcon}
                checkedIcon={CheckboxFilledIcon}
                onChange={handleOnSelectRow(row)}
            />
        );
    }

    function handleRowSelected(row) {
        const handlerRef = getHandler(actions, get(schema, 'selected.isRowSelectedRef'));
        return Boolean(
            callFunc(handlerRef, {
                row,
                excluded,
                selected,
                selectedAll,
            })
        );
    }

    const handleOnSelectRow = row => () => {
        const rowId = getSelectedRowId(row);
        if (!isMultiSelectEnabled) {
            const info = {
                id: rowId,
                label: getSelectedRowLabel(row),
            };
            setExcluded([]);
            setSelectedAll(false);
            setSelected([rowId]);
            setSelectedInfo(info);
            const onSelectHandler = getHandler(actions, get(schema, 'selected.onSelectRef'));
            callFunc(onSelectHandler, rowId);
        } else if (selectedAll && !isMultiSelectByPageEnabled) {
            const excludedIdx = excluded.indexOf(rowId);
            if (excludedIdx === -1) {
                const updatedExcluded = [...excluded, rowId];
                setExcluded(updatedExcluded);
            } else {
                const updatedExcluded = [
                    ...excluded.slice(0, excludedIdx),
                    ...excluded.slice(excludedIdx + 1),
                ];
                setExcluded(updatedExcluded);
            }
        } else {
            const selectedIdx = selected.indexOf(rowId);
            if (selectedIdx === -1) {
                const info = {
                    id: rowId,
                    label: getSelectedRowLabel(row),
                };
                setSelected([...selected, rowId]);
                setSelectedInfo([...selectedInfo, info]);
                const handlerRef = getHandler(actions, 'onChangeSelectedItems');

                if (handlerRef) {
                    callFunc(handlerRef, {
                        row,
                        excluded,
                        selected: [...selected, rowId],
                        selectedAll,
                    });
                }
            } else {
                const updateSelected = [
                    ...selected.slice(0, selectedIdx),
                    ...selected.slice(selectedIdx + 1),
                ];
                const updateSelectedInfo = [
                    ...selectedInfo.slice(0, selectedIdx),
                    ...selectedInfo.slice(selectedIdx + 1),
                ];

                setSelected(updateSelected);
                setSelectedInfo(updateSelectedInfo);
                const handlerRef = getHandler(actions, 'onChangeSelectedItems');

                if (handlerRef) {
                    callFunc(handlerRef, {
                        row,
                        excluded,
                        selected: updateSelected,
                        selectedAll,
                    });
                }
            }
        }
    };

    function getSelectedRowId(row) {
        const rowKey = get(schema, 'selected.key', 'id');
        return get(row, rowKey);
    }

    function getSelectedRowLabel(row) {
        const rowKey = get(schema, 'selected.keyLabel', 'name');
        return get(row, rowKey);
    }

    function handleOnSelectAll() {
        let itemsSelectedByPage = [];
        let itemSelectedInfo = [];
        if (isMultiSelectByPageEnabled) {
            const currentDataInfo = data.map(value => {
                return {
                    id: getSelectedRowId(value),
                    label: getSelectedRowLabel(value),
                };
            });

            const currentDataIds = data.map(value => getSelectedRowId(value));

            if (selectedAll) {
                itemSelectedInfo = _differenceWith(selectedInfo, currentDataInfo, _isEqual);
                itemsSelectedByPage = _differenceWith(selected, currentDataIds, _isEqual);
            } else {
                itemSelectedInfo = [...selectedInfo, ...currentDataInfo];
                itemsSelectedByPage = [...selected, ...currentDataIds];
                itemSelectedInfo = _uniqBy(itemSelectedInfo, 'label');
                itemsSelectedByPage = _uniq(itemsSelectedByPage);
            }
        }
        if (selectedAll) {
            const updatedSelectedInfo = isMultiSelectByPageEnabled ? itemSelectedInfo : [];
            const updatedSelected = isMultiSelectByPageEnabled ? itemsSelectedByPage : [];
            setSelectedAll(false);
            setSelected(updatedSelected);
            setSelectedInfo(updatedSelectedInfo);
            setExcluded([]);

            const handlerRef = getHandler(actions, 'onChangeSelectedItems');
            callFunc(handlerRef, {
                excluded: [],
                selected: updatedSelected,
                selectedAll: false,
            });
        } else {
            const updatedSelectedInfo = isMultiSelectByPageEnabled ? itemSelectedInfo : [];
            const updatedSelected = isMultiSelectByPageEnabled ? itemsSelectedByPage : [];
            setSelectedAll(true);
            setSelected(updatedSelected);
            setSelectedInfo(updatedSelectedInfo);
            setExcluded([]);
            const handlerRef = getHandler(actions, 'onChangeSelectedItems');
            callFunc(handlerRef, {
                excluded: [],
                selected: updatedSelected,
                selectedAll: true,
            });
        }
    }

    function getSelectedItemCount() {
        let count = Number.parseInt(selected.length, 10);

        if (selectedAll && !isMultiSelectByPageEnabled) {
            count = Number.parseInt(pagination.totalRows - excluded.length, 10);
        }

        return count;
    }

    function renderDataList() {
        let dataToShow = [];

        if (isStaticData) {
            const startIndex = (page - 1) * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            dataToShow = _slice(data, startIndex, endIndex);
        } else {
            dataToShow = data;
        }

        const table = get(list, 'table');
        const sortingCustomStyles = get(sorting, 'extraCss', {});

        const listSchema = {
            ...list,
            refs: {
                ...refs,
                onChangeSortRef: 'handleSortChange',
            },
            sorting: {
                ...sorting,
                order,
                orderBy,
                extraCss: {
                    marginBottom: '-2px',
                    ...sortingCustomStyles,
                },
            },
            selected: selectedSchema,
            table: {
                ...table,
                style: {
                    tableLayout: 'fixed',
                },
            },
        };

        const customFirstRowSchema = {
            ...customFirstRow,
        };
        const listActions = {
            ...actions,
            handleSortChange,
            renderSelectable,
        };

        if (showScroll) {
            listSchema.table = scrollStyles;
        }

        return useMemo ? (
            <DataListMemo
                actions={listActions}
                data={dataToShow}
                schema={listSchema}
                customFirstRowData={customFirstRowData}
                customFirstRowSchema={customFirstRowSchema}
            />
        ) : (
            <DataList
                actions={listActions}
                data={dataToShow}
                schema={listSchema}
                customFirstRowData={customFirstRowData}
                customFirstRowSchema={customFirstRowSchema}
                tableId={tableId}
            />
        );
    }

    function handlePageOnChange(e, newPage) {
        setPage(newPage);
    }

    function handleRowsPerPageOnChange({ value: newRowsPerPage }) {
        setRowsPerPage(newRowsPerPage);
    }

    function handleSearch() {
        setSearchSpec(searchInput);
        setPage(1);
    }

    function handlePhoneSearch() {
        setPhoneSearchSpec(phoneSearchInput);
        setPage(1);
    }

    const handleFilterChange = submittedFilters => {
        const onFilterChangeHandler = get(actions, 'onApplyFilter');
        const startDate = dateValidation && get(submittedFilters, dateValidation.startDate);
        const endDate = dateValidation && get(submittedFilters, dateValidation.endDate);
        setPage(1);
        if (!isEmpty(dateValidation) && startDate && endDate) {
            endDate.setHours(23, 59, 59, 999);
            if (startDate > endDate) {
                openModal(MODALS.CONFIRM, {
                    title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                    description: MESSAGES.MODAL.DATE_VALIDATION_MESSAGE,
                });
            } else {
                callFunc(onFilterChangeHandler, submittedFilters);
            }
        } else {
            callFunc(onFilterChangeHandler, submittedFilters);
        }

        setSelectedAll(defaultSelectedAllItems);
        setSelected(defaultSelectedItems);
        setSelectedInfo(defaultSelectedItemsInfo);
        setExcluded([]);
    };

    function handleFilterReset() {
        const onFilterChangeHandler = get(actions, 'onApplyFilter');
        setSelectedFilters(defaultSelectedFilters);
        setFilterOptionSelected(false);
        setPage(1);
        setSelectedAll(defaultSelectedAllItems);
        setSelected(defaultSelectedItems);
        setSelectedInfo(defaultSelectedItemsInfo);
        setExcluded([]);
        callFunc(onFilterChangeHandler, defaultSelectedFilters);
    }

    const onFilterChange = (name, updatedValue, updatedFilters = {}) => {
        const filter = name ? { [name]: updatedValue } : null;
        setSelectedFilters(selectedFilters => ({
            ...selectedFilters,
            ...filter,
            ...updatedFilters,
        }));
        setFilterOptionSelected(true);
    };

    const onAssignPartnerFilter = ({ selected }) => {
        setPartnerIds(selected);
        setPage(1);
        closeModal(MODALS.FILTER_LIST);
    };

    const onAssignClientFilter = ({ selected }) => {
        setCompanyIds(selected);
        setPage(1);
        closeModal(MODALS.FILTER_LIST);
    };

    const handleResetPartnerClientSearch = () => {
        if (actions.handleResetPartnerClientSearch) {
            callFunc(actions.handleResetPartnerClientSearch);
        } else {
            setPartnerIds([]);
            setCompanyIds([]);
            setPage(1);
        }
    };

    const handlePartnerSearchClick = () => {
        const cellTypes = { Check };
        const modalSchema = _cloneDeep(PartnerFilterSchema);

        const ModalProps = get(schema, 'ModalProps', {});
        const showIsActiveColumn = get(ModalProps, 'showIsActiveColumn', false);

        if (showIsActiveColumn === true || showIsActiveColumn === 'true') {
            addSchemaValue(modalSchema, 'list.cellTypes', cellTypes);
            addSchemaValue(modalSchema, 'list.columns', [
                ...modalSchema.list.columns,
                {
                    id: 'isActive',
                    title: 'Active',
                    cellProps: {
                        cellType: 'Check',
                    },
                    sortable: false,
                },
            ]);
        }

        openModal(MODALS.FILTER_LIST, {
            label: PARTNER_LABEL,
            dataKey: 'partners',
            dataKeyId: 'companyId',
            title: USER_LOOKUP.PARTNER_FILTER_TITLE,
            fetchAction: 'fetchPartners',
            responseKey: 'companies',
            schema: modalSchema,
            onAssignFilter: onAssignPartnerFilter,
            handlers: {
                fetchPartners,
            },
            ModalProps,
            actions: [
                {
                    text: MESSAGES.MODAL.ACTIONS.CANCEL,
                    color: 'secondary',
                    onClick: () => closeModal(MODALS.FILTER_LIST),
                },
                {
                    text: MESSAGES.MODAL.CONFIRM.ACTIONS.CONFIRM,
                    color: 'primary',
                },
            ],
        });
    };

    const handleClientSearchClick = () => {
        const cellTypes = { Check };
        const ClientSearchSchema = _cloneDeep(CompanyFilterSchema);

        const ModalProps = get(schema, 'ModalProps', {});
        const showIsActiveColumn = get(ModalProps, 'showIsActiveColumn', false);

        if (showIsActiveColumn === true || showIsActiveColumn === 'true') {
            addSchemaValue(ClientSearchSchema, 'list.cellTypes', cellTypes);
            addSchemaValue(ClientSearchSchema, 'list.columns', [
                ...ClientSearchSchema.list.columns,
                {
                    id: 'isActive',
                    title: 'Active',
                    cellProps: {
                        cellType: 'Check',
                    },
                    sortable: false,
                },
            ]);
        }

        openModal(MODALS.FILTER_LIST, {
            label: COMPANY_LABEL,
            dataKey: 'companies',
            dataKeyId: 'companyId',
            title: USER_LOOKUP.CLIENT_FILTER_TITLE,
            fetchAction: 'fetchClients',
            responseKey: 'companies',
            schema: ClientSearchSchema,
            onAssignFilter: onAssignClientFilter,
            handlers: {
                fetchClients,
            },
            ModalProps,
            actions: [
                {
                    text: MESSAGES.MODAL.ACTIONS.CANCEL,
                    color: 'secondary',
                    onClick: () => closeModal(MODALS.FILTER_LIST),
                },
                {
                    text: MESSAGES.MODAL.CONFIRM.ACTIONS.CONFIRM,
                    color: 'primary',
                },
            ],
        });
    };

    function renderPagination() {
        const isMobile = renderCheck('md', 'less');
        const paginationProps = {
            ...pagination,
            currentPage: page,
            onChangePage: handlePageOnChange,
            onChangeRowsPerPage: handleRowsPerPageOnChange,
            rowsPerPage,
            hideSelectedRow: true,
            totalRows: pagination.totalRows,
            style: {
                padding: 0,
                margin: isMobile ? '0' : '1.576rem 0 0',
            },
        };
        const selectedItemCount = selected && getSelectedItemCount();
        const totalRows = pagination.totalRows || 0;
        const totalResultsLabel =
            selectedItemCount === 0
                ? `${totalRows} Results`
                : `${totalRows} Results   ...   ${selectedItemCount} Selected`;

        return (
            <>
                <Pagination {...paginationProps} />
                {!pagination.hideActions && !isMobile && (
                    <Row justify="space-between" margin="0">
                        <Typography
                            type="paginationResults"
                            color={DEFAULT_COLORS.DARK}
                            gutterTop="0.875rem"
                            gutterBottom="0"
                        >
                            {totalResultsLabel}
                        </Typography>
                    </Row>
                )}
            </>
        );
    }

    function handleResetAll() {
        if (actions.handleResetAll) {
            callFunc(actions.handleResetAll);
        } else {
            handleFilterReset();
            handleSearchReset();
            handlePhoneSearchReset();
            setPartnerIds([]);
            setCompanyIds([]);
            setPage(1);
            setCheckedItems({});
        }
    }

    const leftOffset = search.positionRight ? 6 : 0;
    const searchSize = search.size;

    return (
        <>
            {(!isEmpty(search) || !isEmpty(phoneSearch) || showPartnerSearch) && (
                <Row width="100%" margin="0">
                    {!isEmpty(search) && (
                        <>
                            <Col
                                size={
                                    searchSize || {
                                        xs: 12,
                                        lg: search.removeMargin ? 3 : 6,
                                    }
                                }
                                leftOffset={{
                                    lg: search.removeMargin ? 9 : leftOffset,
                                }}
                                padding="0"
                            >
                                <SearchBox
                                    search={search}
                                    searchInputProps={searchInputProps}
                                    handleSearch={handleSearch}
                                    handleSearchReset={handleSearchReset}
                                    padding={searchBoxStyles.padding}
                                    margin={searchBoxStyles.margin}
                                />
                            </Col>
                        </>
                    )}
                    {!isEmpty(phoneSearch) && (
                        <SearchColStyled size={{ xs: 12, lg: 4 }}>
                            <SearchBox
                                search={phoneSearch}
                                searchInputProps={phoneSearchInputProps}
                                handleSearch={handlePhoneSearch}
                                handleSearchReset={handlePhoneSearchReset}
                            />
                        </SearchColStyled>
                    )}
                    {showPartnerSearch && (
                        <Col size={{ xs: 12, lg: 2 }} style={{ alignSelf: 'center' }}>
                            <Row direction="column" justify="space-between" height="160px">
                                <Button onClick={handleClientSearchClick}>{COMPANY_SEARCH}</Button>
                                <Button onClick={handlePartnerSearchClick}>{PARTNER_SEARCH}</Button>
                                <Button onClick={handleResetPartnerClientSearch}>
                                    {PARTNER_COMPANY_RESET}
                                </Button>
                                {resetAll && <Button onClick={handleResetAll}>{RESET_ALL}</Button>}
                            </Row>
                        </Col>
                    )}
                </Row>
            )}
            {!isEmpty(filters) && (
                <Row width="100%" margin="0">
                    <Col
                        size={{
                            xs: 12,
                            md: rightFilterColumnContent ? 10 : 12,
                        }}
                        padding="0 3rem 0 0"
                    >
                        <Filters
                            actions={actions}
                            filterOptionSelected={filterOptionSelected}
                            filters={filters}
                            selectedFilters={selectedFilters}
                            handleFilterChange={handleFilterChange}
                            handleFilterReset={handleFilterReset}
                            onFilterChange={onFilterChange}
                            filterOptions={filterOptions}
                            checkedItems={checkedItems}
                            setCheckedItems={setCheckedItems}
                        />
                    </Col>
                    {rightFilterColumnContent && (
                        <Col
                            size={{
                                xs: 12,
                                md: 2,
                            }}
                            padding="0"
                        >
                            {rightFilterColumnContent}
                        </Col>
                    )}
                </Row>
            )}
            {!isEmpty(actionButtons) && (
                <Row width="100%" margin="0">
                    <Col size={12} padding="0">
                        <ActionButtons actions={actions} buttons={actionButtons} />
                    </Col>
                </Row>
            )}
            <Row alignItems="flex-start" width="100%" margin="0">
                <Col size={12} padding="0">
                    {renderTopPagination()}
                    {renderDataList()}
                    {renderBottomPagination()}
                    {!hideBackToTop && (
                        <Responsive size="md" value="less">
                            <Row>
                                <BackToTop />
                            </Row>
                        </Responsive>
                    )}
                </Col>
            </Row>
        </>
    );
}

DataListView.propTypes = {
    actions: object,
    currentPage: number,
    customFirstRowData: array,
    data: array,
    dataFilters: object,
    defaultSelectedFilters: object,
    schema: DATA_LIST_VIEW_SCHEMA,
    updateOnMount: bool,
    openModal: func.isRequired,
    closeModal: func.isRequired,
    fetchPartners: func.isRequired,
    fetchClients: func.isRequired,
    hideBackToTop: bool,
    showPartnerSearch: bool,
    resetAll: bool,
    rightFilterColumnContent: oneOfType([element, elementType]),
    useMemo: bool,
    isStaticData: bool,
    tableId: string,
    shouldClearSelectedRows: bool,
};

DataListView.defaultProps = {
    actions: null,
    currentPage: 1,
    customFirstRowData: null,
    data: null,
    dataFilters: null,
    defaultSelectedFilters: {},
    schema: {},
    updateOnMount: true,
    hideBackToTop: false,
    showPartnerSearch: false,
    resetAll: false,
    rightFilterColumnContent: null,
    useMemo: false,
    isStaticData: false,
    tableId: '',
    shouldClearSelectedRows: false,
};

const mapDispatchToProps = {
    fetchClients: adminActions.fetchClients,
    fetchPartners: adminActions.fetchPartners,
    openModal: uiActions.openModal,
    closeModal: uiActions.closeModal,
};

export { DataListView as DataListViewUnwrapped };
export default connect(null, mapDispatchToProps)(React.memo(DataListView));
